// Copyright 2025 Mo-Sys Engineering Ltd. All Rights Reserved.
/**
 * ================================
 * WebSocket Redux Middleware
 * ================================
 *
 * Responsibilities:
 * - Open / close WebSocket connections to server
 * - Handle reconnect logic with backoff
 * - Send messages (with queuing before connection is ready)
 * - Route incoming messages to message-specific handlers
 *
 * IMPORTANT:
 * - This middleware is generic and reusable
 * - Domain logic (equipment, studio, etc.) is injected via wsEventHandlers
 * - Must be registered in Redux store middleware
 */

import { wsConnect, wsDisconnect, wsSend } from './WebsocketActions';

import {
  webSocketConnecting,
  webSocketConnected,
  webSocketClosed,
  webSocketError,
  webSocketMessageError,
} from './WebsocketSlice';

// Default WebSocket URL (development only)
// Production projects should override this via wsConnect payload
const defaultUrl = 'ws://localhost:8000/api/v1/ws';

// Maximum number of automatic reconnection attempts
const MAX_RECONNECT = 5;

/**
 * Factory function to create the WebSocket middleware.
 *
 * @param {Object} wsEventHandlers
 * A map of:
 * {
 *   [eventName: string]: (data, dispatch) => void
 * }
 *
 * Example:
 * {
 *   StudioEquipmentStateChanged: (data, dispatch) => {
 *     dispatch(wsUpdateEquipment(data));
 *   }
 * }
 */
export const createWebSocketMiddleware = (wsEventHandlers = {}) => {
  let ws = null; // Active WebSocket instance
  let lastConnectOptions = null; // { token, url } used for reconnects
  let reconnectAttempts = 0; // Current reconnect attempt count
  let reconnectTimerId = null; // setTimeout reference for reconnect
  const outgoingQueue = []; // Messages queued while socket is not open

  return store => next => action => {
    /**
     * ================================
     * CONNECT
     * ================================
     */
    if (wsConnect.match(action)) {
      // Prevent opening multiple WebSocket connections
      if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
        return next(action);
      }

      // Extract authentication token and optional custom URL
      const { token, url = defaultUrl } = action.payload;
      lastConnectOptions = { token, url };

      // Encode token as subprotocol (simple auth mechanism)
      const encodedToken = btoa(token);

      // Create the WebSocket connection
      ws = new WebSocket(url, encodedToken);

      // Update global WebSocket state
      store.dispatch(webSocketConnecting());

      /**
       * Fired when connection is successfully opened
       */
      ws.onopen = () => {
        reconnectAttempts = 0;

        if (reconnectTimerId) {
          clearTimeout(reconnectTimerId);
          reconnectTimerId = null;
        }

        store.dispatch(webSocketConnected());

        // Send any messages that were queued before connection was ready
        while (outgoingQueue.length > 0 && ws.readyState === WebSocket.OPEN) {
          const msg = outgoingQueue.shift();
          ws.send(JSON.stringify(msg));
        }
      };

      /**
       * Fired when a message is received from the server
       */
      ws.onmessage = event => {
        let payload;

        // Parse JSON safely
        try {
          payload = JSON.parse(event.data);
        } catch {
          return store.dispatch(webSocketMessageError('Invalid WebSocket message received'));
        }

        // Expecting payload.body.{ event, data }
        const body = payload?.body;
        if (!body) {
          return;
        }

        const { event: eventName, data } = body;
        const handler = wsEventHandlers[eventName];

        // Route event to its registered handler
        if (handler) {
          try {
            handler(data, store.dispatch);
          } catch (err) {
            store.dispatch(
              webSocketMessageError(`Error handling WebSocket message: ${err?.message}`)
            );
          }
        }
      };

      /**
       * Fired on low-level WebSocket error
       */
      ws.onerror = () => {
        store.dispatch(webSocketError('WebSocket error'));
      };

      /**
       * Fired when the connection is closed
       */
      ws.onclose = () => {
        store.dispatch(webSocketClosed());
        ws = null;

        // Auto-reconnect with exponential backoff
        if (lastConnectOptions && reconnectAttempts < MAX_RECONNECT) {
          reconnectAttempts += 1;
          reconnectTimerId = setTimeout(() => {
            store.dispatch(wsConnect(lastConnectOptions));
          }, 2000 * reconnectAttempts);
        } else if (reconnectAttempts >= MAX_RECONNECT) {
          store.dispatch(webSocketError('WebSocket failed after multiple reconnects'));
        }
      };
    }

    /**
     * ================================
     * MANUAL DISCONNECT
     * ================================
     */
    if (wsDisconnect.match(action)) {
      if (reconnectTimerId) {
        clearTimeout(reconnectTimerId);
        reconnectTimerId = null;
      }

      if (ws) {
        ws.onclose = null; // Prevent triggering reconnect logic
        ws.close();
        ws = null;
      }

      lastConnectOptions = null;
      reconnectAttempts = 0;

      store.dispatch(webSocketClosed());
    }

    /**
     * ================================
     * SEND MESSAGE
     * ================================
     */
    if (wsSend.match(action)) {
      const message = action.payload;

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(message));
      } else {
        // Queue message until connection is established
        outgoingQueue.push(message);
      }
    }

    return next(action);
  };
};
